import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeFacetModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import { ProductQuantityBreakPricingStyles } from "@insite/content-library/Components/ProductQuantityBreakPricing";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import { ProductListProductImageStyles } from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductPriceForCatalogView from "@WhiteCap/Components/ProductPriceForCatalogView";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    productsWithGroupedCat: ProductModel[];
    maxAttrToShow: number;
}

const mapStateToProps = (state: CustomApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    visibleColumnNames: state.pages.productList.visibleColumnNames,
    tableColumns: state.pages.productList.tableColumns,
    isAuthenticated: state.context.session.isAuthenticated,
    productPriceProductId: state.WhiteCap.products.productPriceProductId,
});

type Props = ReturnType<typeof mapStateToProps> & ProductCardSelections & OwnProps;

export interface ProductListTableStyles {
    wrapper?: InjectableCss;
    dataTable?: DataTableProps;
    descriptionHeader?: DataTableHeaderProps;
    attributesHeader?: DataTableHeaderProps;
    availabilityHeader?: DataTableHeaderProps;
    priceHeader?: DataTableHeaderProps;
    unitOfMeasureHeader?: DataTableHeaderProps;
    actionsHeader?: DataTableHeaderProps;
    descriptionCell?: DataTableCellProps;
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    productImageStyles?: ProductListProductImageStyles;
    attributesCell?: DataTableCellProps;
    availabilityCell?: DataTableCellProps;
    availabilityStyles?: ProductAvailabilityStyles;
    priceCell?: DataTableCellProps;
    productPrice?: ProductPriceStyles;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    unitOfMeasureCell?: DataTableCellProps;
    unitOfMeasureText?: TypographyPresentationProps;
    actionsCell?: DataTableCellProps;
    sponsoredText?: TypographyPresentationProps;
    grpName?: InjectableCss;
    clickableRow?: InjectableCss;
    tableWrapper?: InjectableCss;
}

const actionsStyles: ProductListTableStyles = {
    wrapper: {
        css: css`
            overflow: hidden;
            width: 100%;
        `,
    },
    gridContainer: {
        gap: 0,
        css: css`
            padding: 30px 0;
            min-width: 320px;
        `,
    },
    leftColumnGridItem: {
        width: [4, 4, 4, 3, 3],
        css: css`
            padding-right: 12px;
            flex-direction: column;
        `,
    },
    rightColumnGridItem: {
        width: [8, 8, 8, 9, 9],
        css: css`
            flex-direction: column;
        `,
    },
    productImageStyles: {
        compareCheckbox: {
            css: css`
                align-self: start;
                padding-top: 15px;
            `,
        },
    },
    actionsHeader: {
        css: css`
            position: sticky;
            right: 0;
            width: 200px;
            background-clip: padding-box;
        `,
    },
    descriptionCell: {
        css: css`
            white-space: normal;
        `,
    },
    attributesCell: {
        css: css`
            white-space: normal;
            max-width: 300px;
        `,
    },
    availabilityCell: {
        css: css`
            white-space: normal;
        `,
    },
    availabilityStyles: {
        messageText: {
            css: css`
                white-space: nowrap;
            `,
        },
    },
    actionsCell: {
        css: css`
            position: sticky;
            right: 0;
            width: 200px;
            background-color: inherit;
            background-clip: padding-box;
        `,
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
	grpName:{
		css: css`
			margin-top: 20px;
            margin: 0;
            font-size: 14px;
            line-height: 20px;
            font-weight: 500;
			background-color: #ddd;
			padding: 5px 10px;
		`
	},
    dataTable:{
        css: css`
            thead{
                tr{
                    th{
                        height: unset;
                        padding: 0px 12px;
                    }
                }
            }
            tbody{
                tr{
                    td{
                        height: unset;
                        padding: 0px 12px;
                    }
                }
            }
        `
    },
    clickableRow:{
        css: css`
            cursor: pointer;
            &:hover{
                background-color: #f9f5c2;
            }
        `
    },
    tableWrapper:{
        css: css`
            margin-bottom: 25px;
        `
    }
};

const styles = actionsStyles;

const ProductListProductCatalog = ({
    productsWithGroupedCat,
    productSettings,
    tableColumns,
    isAuthenticated,
    maxAttrToShow,
    productPriceProductId,
}: Props) => {
    const tableContainerRef = useRef<HTMLDivElement>(null);
    const [tableWidth, setTableWidth] = useState("100%");

    useEffect(() => {
        const updateWidth = () => {
            if (tableContainerRef.current) {
                // Get the full scrollable width, not just the visible width
                setTableWidth(`${tableContainerRef.current.scrollWidth}px`);
            }
        };

        updateWidth(); // Set initial width
        window.addEventListener("resize", updateWidth); // Update on window resize

        return () => window.removeEventListener("resize", updateWidth);
    }, []);

    const groupProductsByProductGrouping = (productsWithGroupedCat: ProductModel[]) => {
        const groupedProducts: Record<string, ProductModel[]> = {};

        productsWithGroupedCat?.forEach(product => {
            const productGroupingName = product.properties?.productGrouping || "";
            if (!groupedProducts[productGroupingName]) {
                groupedProducts[productGroupingName] = [];
            }
            groupedProducts[productGroupingName].push(product);
        });

        return groupedProducts;
    };

    const groupedProducts = groupProductsByProductGrouping(productsWithGroupedCat);

    const getAttributeValueForSection = (
        facet: AttributeTypeFacetModel,
        productsWithGroupedCat: any,
        maxAttrToShow: number = 3
    ) => {
        if (facet.name === "Brand") {
            return null; // Exclude "Brand"
        }

        const attributeValues = productsWithGroupedCat.attributeTypes
            ?.filter((o: any) => o.id === facet.attributeTypeId) // Find matching attribute type
            ?.flatMap((o: any) => o.attributeValues) // Extract attributeValues array
            ?.sort((a: any, b: any) => a.sortOrder - b.sortOrder) // Sort by sortOrder (ascending)
            ?.slice(0, maxAttrToShow) // Limit to maxAttrToShow after sorting
            ?.map((o: any) => o.valueDisplay) // Extract valueDisplay
            .join(", ");

        if (attributeValues) {
            return attributeValues;
        }

        if (facet.name === "Product Line" && productsWithGroupedCat.productLine) {
            return productsWithGroupedCat.productLine.name;
        }

        return null;
    };

    const filteredTableColumns = tableColumns.filter((col: any) => col.name !== "Brand");

    const visibleTableColumns =  [...filteredTableColumns] // Clone the array to prevent modifying the original
        ?.sort((a: any, b: any) => {
            if (a.sortOrder === b.sortOrder) {
                return a.name.localeCompare(b.name); // Sort alphabetically if sortOrder is the same
            }
            return a.sortOrder - b.sortOrder; // Otherwise, sort by sortOrder
        })
        ?.slice(0, maxAttrToShow) || [];

    return (
        <StyledWrapper {...styles.wrapper}>
            {Object.keys(groupedProducts)
            .sort((a, b) => {
                if (!a) return 1; // Move empty strings to the bottom
                if (!b) return -1;
                return a.localeCompare(b);
            })
            .map((group, idx) => (
                <StyledWrapper {...styles.tableWrapper} key={group}>
                    <StyledWrapper style={{ width: "100%" }}>
                        <Typography
                            {...styles.grpName}
                            style={{
                                width: tableWidth,
                                display: "block",
                                textAlign: "left",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                minHeight: "30px",
                            }}
                            variant="h4"
                        >
                            {group}
                        </Typography>
                    </StyledWrapper>
                    <StyledWrapper
                        ref={tableContainerRef}
                        style={{
                            overflowX: "auto",
                            maxWidth: "100%",
                            wordBreak: "keep-all",
                            whiteSpace: "nowrap",
                            scrollbarWidth: "thin",
                        }}
                    >
                        <DataTable
                            {...styles.dataTable}
                            style={{
                                minWidth: "100%",
                                width: "max-content", // Expands to fit content
                                wordBreak: "break-word", // Breaks long words
                                whiteSpace: "normal", // Allows wrapping
                            }}
                        >
                            <DataTableHead>
                                {visibleTableColumns.map((facet: any) => (
                                    <DataTableHeader key={`${facet.name}_tableHeader`} {...styles.attributesHeader}>
                                        {facet.nameDisplay ||
                                            (facet.name === "Product Line" ? translate("Product Line") : facet.name)}
                                    </DataTableHeader>
                                ))}
                                <DataTableHeader {...styles.descriptionHeader}>{translate("Product#")}</DataTableHeader>
                                {isAuthenticated && (
                                    <DataTableHeader {...styles.priceHeader}>{translate("Price")}</DataTableHeader>
                                )}
                            </DataTableHead>
                            <DataTableBody>
                                {groupedProducts[group].map((product) => {
                                    const productPriceInfo = productPriceProductId?.[product?.id] ?? null;
                                    const unitOfMeasure =
                                        (product.customerUnitOfMeasure ||
                                            product.unitOfMeasures?.find(o => o.isDefault)?.unitOfMeasure) ??
                                        "";
                                    let qtyAdjusted = Math.max(product.minimumOrderQty, 1);
                                    if (unitOfMeasure) {
                                        const uom = product.unitOfMeasures?.find(
                                            o => o.unitOfMeasure === unitOfMeasure,
                                        );
                                        if (uom && product.minimumOrderQty > 0 && uom.qtyPerBaseUnitOfMeasure > 0) {
                                            qtyAdjusted = Math.ceil(
                                                product.minimumOrderQty / uom.qtyPerBaseUnitOfMeasure,
                                            );
                                        }
                                    }

                                    const productInfo = {
                                        productId: product.id,
                                        qtyOrdered: qtyAdjusted,
                                        unitOfMeasure,
                                        productDetailPath: product.canonicalUrl,
                                    };

                                    if (!productInfo) {
                                        return null;
                                    }

                                    const productDetailPath = product.canonicalUrl || "#";

                                    return (
                                        <DataTableRow
                                            key={product.id}
                                            onClick={() => {
                                                window.location.href = productDetailPath;
                                            }}
                                            {...styles.clickableRow}
                                        >
                                            {visibleTableColumns.map((facet: any) => (
                                                <DataTableCell key={facet.name} {...styles.attributesCell}>
                                                    {getAttributeValueForSection(facet, product, maxAttrToShow)}
                                                </DataTableCell>
                                            ))}
                                            <DataTableCell {...styles.descriptionCell}>
                                                <Link href={productDetailPath}> {product.productNumber}</Link>
                                            </DataTableCell>
                                            {isAuthenticated && (
                                                <DataTableCell {...styles.priceCell}>
                                                    <ProductPriceForCatalogView
                                                        product={product}
                                                        productInfo={productInfo}
                                                        productPriceProductInfo={productPriceInfo}
                                                        showLabel={false}
                                                        showUnitOfMeasure={true}
                                                        showPack={true}
                                                        showSavingsAmount={productSettings.showSavingsAmount}
                                                        showSavingsPercent={productSettings.showSavingsPercent}
                                                        extendedStyles={styles.productPrice}
                                                    />
                                                </DataTableCell>
                                            )}
                                        </DataTableRow>
                                    );
                                })}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(ProductListProductCatalog);
